import { Box, Button, Typography, FormControl, RadioGroup, FormControlLabel, Radio, TextField } from "@mui/material";
import { Modal } from "components/common/Modal";
import { useState } from "react";

const CANCELLATION_REASONS = [
  { value: "incorrect address", label: "Incorrect Address" },
  {
    value: "incorrect return location",
    label: "Incorrect return location",
    subtitle: "i.e. selected DTR when it should go back to an office",
  },
  { value: "duplicate order", label: "Duplicate Order" },
  { value: "employee already returned their device", label: "Employee already returned their device" },
  { value: "other", label: "Other" },
];

interface AssetRetrievalCancellationReasonModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  onSubmit: (reason: string) => void;
  loading?: boolean;
}

export function AssetRetrievalCancellationReasonModal({
  open,
  setOpen,
  onSubmit,
  loading = false,
}: AssetRetrievalCancellationReasonModalProps) {
  const [selectedReason, setSelectedReason] = useState<string>("");
  const [customReason, setCustomReason] = useState<string>("");

  const handleReasonChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedReason(event.target.value);
    if (event.target.value !== "other") {
      setCustomReason("");
    }
  };

  const finalReason = selectedReason === "other" ? customReason : selectedReason;

  const handleClose = () => {
    setSelectedReason("");
    setCustomReason("");
  };

  const handleSubmit = () => {
    if (finalReason.trim()) {
      onSubmit(finalReason);
      setOpen(false);
      handleClose();
    }
  };

  const isSubmitDisabled = !selectedReason || (selectedReason === "other" && !customReason.trim());

  return (
    <Modal open={open} setOpen={setOpen} onClose={handleClose}>
      <Box p={1}>
        <Typography mt={1} variant="h5" component="h2" fontWeight={800}>
          Reason for Cancellation
        </Typography>

        <Box mt={3} sx={{ textAlign: "left" }}>
          <FormControl component="fieldset" fullWidth>
            <RadioGroup value={selectedReason} onChange={handleReasonChange} sx={{ gap: 1.5 }}>
              {CANCELLATION_REASONS.map((reason) => (
                <Box key={reason.value}>
                  <FormControlLabel
                    value={reason.value}
                    control={<Radio />}
                    label={
                      <Box>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {reason.label}
                        </Typography>
                        {reason.subtitle && (
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.875rem", ml: 0 }}>
                            {reason.subtitle}
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                  {reason.value === "other" && selectedReason === "other" && (
                    <Box mt={1.5} ml={4.5}>
                      <TextField
                        fullWidth
                        multiline
                        rows={3}
                        placeholder=""
                        value={customReason}
                        onChange={(e) => setCustomReason(e.target.value)}
                        variant="outlined"
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            backgroundColor: "#f5f5f5",
                          },
                        }}
                      />
                    </Box>
                  )}
                </Box>
              ))}
            </RadioGroup>
          </FormControl>
        </Box>

        <Box mt={4} sx={{ display: "flex", gap: 1, alignItems: "center", justifyContent: "center" }}>
          <Button
            variant="outlined"
            onClick={() => {
              setOpen(false);
              handleClose();
            }}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSubmit} disabled={loading || isSubmitDisabled}>
            Request Cancellation
          </Button>
        </Box>
      </Box>
    </Modal>
  );
}
